import React from 'react';
import styled from 'styled-components';

// Custom SVG icons as React components
const PersonalisedGuidanceIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 48 48">
    <circle cx="24" cy="16" r="8" fill="#f7d95c"/>
    <path d="M24 28c-7 0-12 3-12 7v3h24v-3c0-4-5-7-12-7z" fill="#fff"/>
    <path d="M24 28c-7 0-12 3-12 7v3h24v-3c0-4-5-7-12-7z"/>
    <circle cx="24" cy="16" r="8"/>
    <path d="M24 20v-4"/>
    <path d="M24 16h2"/>
  </svg>
);

const TrustedPartnershipsIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 48 48">
    <rect x="10" y="18" width="28" height="18" rx="4" fill="#f7d95c"/>
    <rect x="10" y="18" width="28" height="18" rx="4"/>
    <path d="M24 18v-6m0 0l-4 4m4-4l4 4"/>
    <path d="M16 36v2m16-2v2"/>
  </svg>
);

const SaveTimeMoneyIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 48 48">
    <rect x="8" y="8" width="32" height="32" rx="8" fill="#f7d95c"/>
    <rect x="8" y="8" width="32" height="32" rx="8"/>
    <path d="M24 16v8l6 4"/>
    <circle cx="24" cy="24" r="16"/>
    <text x="24" y="40" textAnchor="middle" fontSize="10" fill="currentColor" fontFamily="Arial">$</text>
  </svg>
);

const SimpleProcessIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 48 48">
    <rect x="10" y="10" width="28" height="28" rx="6" fill="#f7d95c"/>
    <rect x="10" y="10" width="28" height="28" rx="6"/>
    <path d="M18 18h12v12H18z"/>
    <path d="M24 18v12"/>
    <path d="M18 24h12"/>
  </svg>
);

const ReliableComplianceIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 48 48">
    <rect x="10" y="10" width="28" height="28" rx="6" fill="#f7d95c"/>
    <rect x="10" y="10" width="28" height="28" rx="6"/>
    <path d="M24 18v12"/>
    <path d="M18 24h12"/>
    <circle cx="24" cy="24" r="8"/>
  </svg>
);

const TransparentIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 48 48">
    <rect x="8" y="8" width="32" height="32" rx="8" fill="#f7d95c"/>
    <rect x="8" y="8" width="32" height="32" rx="8"/>
    <path d="M16 24h16M16 28h16M16 20h16"/>
    <circle cx="24" cy="16" r="3"/>
  </svg>
);

const WhyChooseContainer = styled.section`
  width: 100%;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  background: white;
  box-sizing: border-box;

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    padding: 3rem 0;
    min-height: 55vh;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    padding: 3.5rem 0;
    min-height: 58vh;
  }

  @media (max-width: 700px) {
    padding: 2rem 1rem;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
    min-height: 50vh;
  }

  /* Very small screens */
  @media (max-width: 360px) {
    padding: 1.25rem 0.5rem;
    min-height: 45vh;
  }
`;

const WhyHeader = styled.div`
  max-width: 800px;
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 1100px) {
    margin-bottom: 2rem;
  }

  @media (max-width: 700px) {
    margin-bottom: 1.125rem;
  }
`;

const WhyTitle = styled.h2`
  font-size: 3.2rem;
  font-weight: 800;
  color: ${props => props.theme.colors.heading};
  margin-bottom: 1.125rem;
  line-height: 1.08;
  letter-spacing: -2px;

  @media (max-width: 700px) {
    font-size: 1.6rem;
  }
`;

const WhyDescription = styled.p`
  font-size: 1.13rem;
  color: #444;
  margin-bottom: 0;
  line-height: 1.5;
`;

const WhyCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.25rem 2rem;
  width: 100%;
  max-width: 1200px;
  align-items: stretch;
  justify-items: center;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem 1.125rem;
    max-width: 98vw;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    max-width: 95vw;
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
    max-width: 92vw;
    padding: 0 0.25rem;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    max-width: 90vw;
    padding: 0;
  }
`;

const WhyCard = styled.div`
  background: white;
  border-radius: 28px;
  box-shadow: 0 4px 24px rgba(143, 226, 0, 0.07);
  border: 2px solid #e6f3df;
  padding: 2.25rem 1.75rem 2rem 1.75rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  max-width: 370px;
  width: 100%;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 8px 32px rgba(143, 226, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.colors.secondary};
    transform: translateY(-6px) scale(1.03);
    z-index: 2;
  }

  @media (max-width: 700px) {
    padding: 1.5rem 1rem 1.25rem 1rem;
    max-width: 95vw;
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    padding: 1.25rem 0.75rem 1rem 0.75rem;
    max-width: 92vw;
    margin: 0 auto;
  }

  @media (max-width: 360px) {
    padding: 1rem 0.5rem 0.75rem 0.5rem;
    max-width: 90vw;
    margin: 0 auto;
  }
`;

const WhyCardIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.125rem;
  border: 2px solid ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.secondary};

  @media (max-width: 700px) {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    margin-bottom: 0.625rem;
  }
`;

const WhyCardTitle = styled.h3`
  font-size: 1.18rem;
  font-weight: 700;
  color: ${props => props.theme.colors.heading};
  margin-bottom: 0.625rem;
  line-height: 1.2;

  @media (max-width: 700px) {
    font-size: 1.1rem;
  }
`;

const WhyCardDescription = styled.p`
  font-size: 1.01rem;
  color: #444;
  line-height: 1.5;
  margin-bottom: 0;

  @media (max-width: 700px) {
    font-size: 0.95rem;
  }
`;

const WhyChooseSection = () => {
  const benefits = [
    {
      icon: PersonalisedGuidanceIcon,
      title: 'Personalised Guidance',
      description: 'We take the time to understand your skills, goals and background, so we can tailor the RPL process to fit your unique career journey.'
    },
    {
      icon: TrustedPartnershipsIcon,
      title: 'Trusted RTO Partnerships',
      description: 'Your qualifications matter. We work only with approved Registered Training Organisations (RTOs) to ensure every qualification you receive is nationally recognised and compliant with Australian standards.'
    },
    {
      icon: SaveTimeMoneyIcon,
      title: 'Save Time and Money',
      description: 'No need to sit through training you already know. We help you turn your existing skills and knowledge into formal qualifications quickly, so you can move forward without repeating what you’ve already mastered.'
    },
    {
      icon: SimpleProcessIcon,
      title: 'Simple, Stress-Free Process',
      description: 'Our team handles the details — paperwork, evidence gathering and communication with the RTO — so you can focus on your work while we take care of the rest.'
    },
    {
      icon: ReliableComplianceIcon,
      title: 'Reliable Compliance',
      description: 'We make sure everything we do meets industry regulations and best practice standards. You can be confident your qualifications are credible and respected.'
    },
    {
      icon: TransparentIcon,
      title: 'Transparent & Upfront',
      description: 'No hidden costs or surprises — we clearly explain every step, what’s included, and what you’ll need to provide, so you always know where you stand.'
    }
  ];

  return (
    <WhyChooseContainer>
      <WhyHeader>
        <WhyTitle>Why Choose RPL Support</WhyTitle>
        <WhyDescription>
          At RPL Support, we know your experience is valuable — and we're here to make sure it counts. Here's why skilled workers and professionals trust us with their Recognition of Prior Learning (RPL):
        </WhyDescription>
      </WhyHeader>
      
      <WhyCards>
        {benefits.map((benefit, index) => (
          <WhyCard key={index}>
            <WhyCardIcon>
              <benefit.icon />
            </WhyCardIcon>
            <WhyCardTitle>{benefit.title}</WhyCardTitle>
            <WhyCardDescription>{benefit.description}</WhyCardDescription>
          </WhyCard>
        ))}
      </WhyCards>
    </WhyChooseContainer>
  );
};

export default WhyChooseSection;